<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <v-skeleton-loader
        v-if="loading"
        type="table"
        class="mx-4"
      ></v-skeleton-loader>

      <template v-else>
        <div class="d-flex justify-end px-4 pt-3">
          <v-btn small color="primary" outlined @click="toggleDisplayUnit">
            單位: {{ displayUnit === "hours" ? "小時" : "天" }}
            <v-icon small class="ml-1">mdi-swap-horizontal</v-icon>
          </v-btn>
        </div>

        <v-simple-table class="py-3">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">員工</th>
                <th class="text-center">特休可用/總額 ({{ unitText }})</th>
                <template v-if="!showAnnualOnly">
                  <th class="text-center">事假可用/總額 ({{ unitText }})</th>
                  <th class="text-center">病假可用/總額 ({{ unitText }})</th>
                  <th class="text-center">生理假可用/總額 ({{ unitText }})</th>
                  <th class="text-center">
                    公傷病假可用/總額 ({{ unitText }})
                  </th>
                  <th class="text-center">婚假可用/總額 ({{ unitText }})</th>
                  <th class="text-center">產檢假可用/總額 ({{ unitText }})</th>
                  <th class="text-center">產假可用/總額 ({{ unitText }})</th>
                  <th class="text-center">
                    陪產檢及陪產假可用/總額 ({{ unitText }})
                  </th>
                  <th class="text-center">喪假可用/總額 ({{ unitText }})</th>
                  <th class="text-center">
                    家庭照顧假可用/總額 ({{ unitText }})
                  </th>
                  <!-- <th class="text-center">最後更新</th> -->
                  <th class="text-center">修改</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="employee in employees" :key="employee.id">
                <td width="80px">{{ employee.name }}</td>
                <td class="text-center">
                  {{ convertMinutesToDays(employee.annualLeave.available) }}
                  /
                  {{ convertMinutesToDays(employee.annualLeave.max) }}
                </td>
                <template v-if="!showAnnualOnly">
                  <td class="text-center">
                    {{ convertMinutesToDays(employee.personalLeave.available) }}
                    /
                    {{ convertMinutesToDays(employee.personalLeave.max) }}
                  </td>
                  <td class="text-center">
                    {{ convertMinutesToDays(employee.sickLeave.available) }}
                    /
                    {{ convertMinutesToDays(employee.sickLeave.max) }}
                  </td>
                  <td class="text-center">
                    {{
                      convertMinutesToDays(employee.menstrualLeave.available)
                    }}
                    /
                    {{ convertMinutesToDays(employee.menstrualLeave.max) }}
                  </td>
                  <td class="text-center">
                    {{
                      convertMinutesToDays(
                        employee.occupationalSickLeave.available
                      )
                    }}
                    /
                    {{
                      convertMinutesToDays(employee.occupationalSickLeave.max)
                    }}
                  </td>
                  <td class="text-center">
                    {{ convertMinutesToDays(employee.marriageLeave.available) }}
                    /
                    {{ convertMinutesToDays(employee.marriageLeave.max) }}
                  </td>
                  <td class="text-center">
                    {{
                      convertMinutesToDays(
                        employee.prenatalCheckupLeave.available
                      )
                    }}
                    /
                    {{
                      convertMinutesToDays(employee.prenatalCheckupLeave.max)
                    }}
                  </td>
                  <td class="text-center">
                    {{
                      convertMinutesToDays(employee.maternityLeave.available)
                    }}
                    /
                    {{ convertMinutesToDays(employee.maternityLeave.max) }}
                  </td>
                  <td class="text-center">
                    {{
                      convertMinutesToDays(employee.paternityLeave.available)
                    }}
                    /
                    {{ convertMinutesToDays(employee.paternityLeave.max) }}
                  </td>
                  <td class="text-center">
                    {{
                      convertMinutesToDays(employee.bereavementLeave.available)
                    }}
                    /
                    {{ convertMinutesToDays(employee.bereavementLeave.max) }}
                  </td>
                  <td class="text-center">
                    {{
                      convertMinutesToDays(employee.familyCareLeave.available)
                    }}
                    /
                    {{ convertMinutesToDays(employee.familyCareLeave.max) }}
                  </td>
                  <!-- <td class="text-center">
                    <formatted-date :date="employee.lastRefreshed" />
                  </td> -->
                  <td class="text-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="openAddQuotaDialog(employee)"
                        >
                          <v-icon small color="red">mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>修改可用額度</span>
                    </v-tooltip>
                  </td>
                </template>
              </tr>
              <tr v-if="!employees || employees.length === 0">
                <td
                  :colspan="showAnnualOnly ? 2 : 15"
                  class="text-center py-4 grey--text"
                >
                  暫無資料
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <v-dialog v-model="addQuotaDialog" max-width="50%">
          <v-card>
            <v-card-title class="headline">新增額度</v-card-title>
            <v-card-text>
              <div class="text-lg mb-2" v-if="selectedEmployeeNames.length">
                <span class="red--text">選擇員工：</span>
                <span class="font-weight-medium">
                  {{ selectedEmployeeNames.join("、") }}
                </span>
              </div>
              <v-select
                v-model="selectedLeaveField"
                :items="leaveTypeOptions"
                item-text="name"
                item-value="field"
                label="請假類型"
                outlined
                dense
                class="mb-4"
              />
              <v-textarea
                v-if="selectedLeaveDescription"
                :value="selectedLeaveDescription"
                label="給假日數"
                readonly
                auto-grow
                outlined
                dense
                class="mb-4"
              />
              <v-text-field
                v-model.number="quotaDays"
                type="number"
                min="0"
                label="新增天數 (將換算成 8 小時/天)"
                outlined
                dense
              />
              <div v-if="quotaError" class="error--text text-caption mt-1">
                {{ quotaError }}
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="closeAddQuotaDialog" :disabled="savingQuota">
                取消
              </v-btn>
              <v-btn
                class="btn-primary bg-gradient-primary px-4"
                text
                @click="submitAddQuota"
                :loading="savingQuota"
              >
                新增額度
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <div class="text-center my-4" v-if="pageCount > 1">
          <v-pagination
            v-model="currentPage"
            :length="pageCount"
            @input="fetchLeaveBalances"
            prev-icon="fa fa-angle-left"
            next-icon="fa fa-angle-right"
            color="#cb0c9f"
            circle
          ></v-pagination>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "LeaveBalancesWidget",
  components: {},
  props: {
    selectedCompany: {
      type: Object,
      default: () => null,
    },
    showAnnualOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      employees: [],
      currentPage: 1,
      itemsPerPage: 15,
      totalItems: 0,
      loading: false,
      fetchError: false,
      totalPages: 1,
      addQuotaDialog: false,
      selectedLeaveField: null,
      quotaDays: null,
      quotaError: "",
      savingQuota: false,
      selectedLeaveBalanceIds: [],
      selectedEmployeeNames: [],
      leaveTypeOptions: [
        { name: "生理假", field: "menstrual_leave" },
        { name: "公傷病假", field: "occupational_sick_leave" },
        { name: "婚假", field: "marriage_leave" },
        { name: "產檢假", field: "prenatal_checkup_leave" },
        { name: "產假", field: "maternity_leave" },
        { name: "陪產檢及陪產假", field: "paternity_leave" },
        { name: "喪假", field: "bereavement_leave" },
        { name: "家庭照顧假", field: "family_care_leave" },
        { name: "原住民族勞工歲時祭儀假", field: "indigenous_ritual_leave" },
      ],
      defaultQuotaDaysByField: {
        marriage_leave: 8,
        prenatal_checkup_leave: 7,
        paternity_leave: 7,
        maternity_leave: 56,
      },
      leaveTypeDescriptions: {
        maternity_leave:
          "給假日數：\n⑴懷孕未滿兩個月流產者，含例假日給予五日，得併入病假天數。\n⑵懷孕未兩個月以上未滿三個月流產者，含例假日給予一週，得併入病假天數。\n⑶懷孕三個月以上流產者，含例假日給予四週。\n⑷分娩（生產）者，含例假日給予八週。",
        bereavement_leave:
          "給假日數：\n⑴父母、養父母、繼父母、配偶喪亡，給予八日。\n⑵祖父母、外祖父母、子女及配偶之父母、繼父母、養父母喪亡，給予六日。\n⑶兄弟姊妹及配偶之祖父母、外祖父母、曾祖父母喪亡，給予三日。",
        marriage_leave:
          "給假日數：八日\n申請方式及證明文件：\n⑴於請假日前七日提出申請。\n⑵須於登記日前十日起三個月內申請休畢。\n⑶須附喜帖正本或結婚證書影本。",
        prenatal_checkup_leave:
          "給假日數：七日\n申請方式及證明文件：須於七日前提出申請，銷假時需附產檢證明。",
        paternity_leave:
          "給假日數：七日\n申請方式及證明文件：\n⑴須於請假的前七日提出申請。\n⑵臨時性（緊急）狀況者，先以電話（或通訊軟體）回報主管口頭請假確認後，於復職三日內完成銷假手續。",
        menstrual_leave:
          "給假日數：\n⑴每月得請生理假一日。\n⑵全年生理假日數未逾三日，不併入病假計算，其餘日數併入病假計算。",
        indigenous_ritual_leave:
          "給假日數：依勞工戶籍地公所發佈之正式公文為申請憑證。\n申請方式及證明文件：\n①於請假日前一個月提出申請。\n②勞工提供戶籍謄本或戶口名簿影本等可資證明其族別之相關文件。",
        occupational_sick_leave:
          "給假日數：依職災診斷證明內醫師診斷建議之休養日數給假，必要時依鑑定報告之建議給予。",
        family_care_leave:
          "給假日數：全年合計七日（56小時）。\n申請方式及證明文件：\n⑴須於請假的前七日提出申請。\n⑵臨時性（緊急）狀況者，先以電話（或通訊軟體）回報單位主管口頭請假確認後，於復職三日內完成銷假手續，若未依規定則依事假辦理。\n⑶須附預防接種登記卡、醫師診斷證明書或相關證明文件（如停課通知書）。\n⑷若未檢附文件者，一律視為事假。",
      },
      displayUnit: localStorage.getItem("leave_duration_unit") || "hours",
    };
  },
  computed: {
    pageCount() {
      return this.totalPages || 1;
    },
    selectedLeaveDescription() {
      if (!this.selectedLeaveField) return "";
      return this.leaveTypeDescriptions[this.selectedLeaveField] || "";
    },
    unitText() {
      return this.displayUnit === "hours" ? "小時" : "天";
    },
  },
  async mounted() {
    await this.fetchLeaveBalances();
    window.addEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit
    );
  },
  watch: {
    selectedCompany: {
      handler() {
        this.currentPage = 1;
        this.fetchLeaveBalances();
      },
      deep: true,
    },
    selectedLeaveField(newValue) {
      if (!newValue) return;
      const preset = this.defaultQuotaDaysByField[newValue];
      if (preset !== undefined) {
        this.quotaDays = preset;
      }
    },
  },
  methods: {
    convertMinutesToDays(minutes) {
      if (minutes == null) return "0";
      const val = parseFloat(minutes);
      if (isNaN(val)) return "0";
      if (this.displayUnit === "hours") {
        const hours = val / 60;
        return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
      }
      const days = val / 480;
      return Number.isInteger(days) ? days.toString() : days.toFixed(1);
    },
    syncDisplayUnit() {
      this.displayUnit = localStorage.getItem("leave_duration_unit") || "hours";
    },
    toggleDisplayUnit() {
      this.displayUnit = this.displayUnit === "hours" ? "days" : "hours";
      localStorage.setItem("leave_duration_unit", this.displayUnit);
      window.dispatchEvent(new Event("leave-duration-unit-changed"));
    },
    async fetchLeaveBalances() {
      console.log("fetchLeaveBalances");
      if (this.loading) return;
      this.loading = true;
      this.fetchError = false;

      try {
        const currentCompanyID = this.selectedCompany
          ? this.selectedCompany.id
          : 1;
        const queryParams = new URLSearchParams({
          page: this.currentPage,
          limit: this.itemsPerPage,
          company_id: currentCompanyID,
        });
        const response = await this.$axios.get(
          `/api/leave-balances?${queryParams}`
        );

        const data = response.data || {};
        const rows = data.data || [];
        const pagination = data.pagination || {};

        this.employees = rows.map((item) => {
          const annualLeaveAvailable = parseFloat(item.annual_leave) || 0;
          const annualLeaveMax = parseFloat(item.annual_leave_max) || 0;
          const personalLeaveAvailable = parseFloat(item.personal_leave) || 0;
          const personalLeaveMax = parseFloat(item.personal_leave_max) || 0;
          const sickLeaveAvailable = parseFloat(item.sick_leave) || 0;
          const sickLeaveMax = parseFloat(item.sick_leave_max) || 0;
          const menstrualLeaveAvailable = parseFloat(item.menstrual_leave) || 0;
          const menstrualLeaveMax = parseFloat(item.menstrual_leave_max) || 0;
          const occupationalSickLeaveAvailable =
            parseFloat(item.occupational_sick_leave) || 0;
          const occupationalSickLeaveMax =
            parseFloat(item.occupational_sick_leave_max) || 0;
          const marriageLeaveAvailable = parseFloat(item.marriage_leave) || 0;
          const marriageLeaveMax = parseFloat(item.marriage_leave_max) || 0;
          const prenatalCheckupLeaveAvailable =
            parseFloat(item.prenatal_checkup_leave) || 0;
          const prenatalCheckupLeaveMax =
            parseFloat(item.prenatal_checkup_leave_max) || 0;
          const maternityLeaveAvailable = parseFloat(item.maternity_leave) || 0;
          const maternityLeaveMax = parseFloat(item.maternity_leave_max) || 0;
          const paternityLeaveAvailable = parseFloat(item.paternity_leave) || 0;
          const paternityLeaveMax = parseFloat(item.paternity_leave_max) || 0;
          const bereavementLeaveAvailable =
            parseFloat(item.bereavement_leave) || 0;
          const bereavementLeaveMax =
            parseFloat(item.bereavement_leave_max) || 0;
          const officialLeaveAvailable = parseFloat(item.official_leave) || 0;
          const officialLeaveMax = parseFloat(item.official_leave_max) || 0;
          const familyCareLeaveAvailable =
            parseFloat(item.family_care_leave) || 0;
          const familyCareLeaveMax =
            parseFloat(item.family_care_leave_max) || 0;

          const annualLeaveUsed = annualLeaveMax - annualLeaveAvailable;
          const personalLeaveUsed = personalLeaveMax - personalLeaveAvailable;
          const sickLeaveUsed = sickLeaveMax - sickLeaveAvailable;

          const totalUsed = annualLeaveUsed + sickLeaveUsed + personalLeaveUsed;
          const totalRemaining =
            annualLeaveAvailable + sickLeaveAvailable + personalLeaveAvailable;

          return {
            id: item.user_id,
            leaveBalanceId: item.id,
            name: item.user ? item.user.name : "Unknown",
            annualLeave: {
              used: annualLeaveUsed,
              available: annualLeaveAvailable,
              max: annualLeaveMax,
            },
            personalLeave: {
              used: personalLeaveUsed,
              available: personalLeaveAvailable,
              max: personalLeaveMax,
            },
            sickLeave: {
              used: sickLeaveUsed,
              available: sickLeaveAvailable,
              max: sickLeaveMax,
            },
            menstrualLeave: {
              used: menstrualLeaveMax - menstrualLeaveAvailable,
              available: menstrualLeaveAvailable,
              max: menstrualLeaveMax,
            },
            occupationalSickLeave: {
              used: occupationalSickLeaveMax - occupationalSickLeaveAvailable,
              available: occupationalSickLeaveAvailable,
              max: occupationalSickLeaveMax,
            },
            marriageLeave: {
              used: marriageLeaveMax - marriageLeaveAvailable,
              available: marriageLeaveAvailable,
              max: marriageLeaveMax,
            },
            prenatalCheckupLeave: {
              used: prenatalCheckupLeaveMax - prenatalCheckupLeaveAvailable,
              available: prenatalCheckupLeaveAvailable,
              max: prenatalCheckupLeaveMax,
            },
            maternityLeave: {
              used: maternityLeaveMax - maternityLeaveAvailable,
              available: maternityLeaveAvailable,
              max: maternityLeaveMax,
            },
            paternityLeave: {
              used: paternityLeaveMax - paternityLeaveAvailable,
              available: paternityLeaveAvailable,
              max: paternityLeaveMax,
            },
            bereavementLeave: {
              used: bereavementLeaveMax - bereavementLeaveAvailable,
              available: bereavementLeaveAvailable,
              max: bereavementLeaveMax,
            },
            officialLeave: {
              used: officialLeaveMax - officialLeaveAvailable,
              available: officialLeaveAvailable,
              max: officialLeaveMax,
            },
            familyCareLeave: {
              used: familyCareLeaveMax - familyCareLeaveAvailable,
              available: familyCareLeaveAvailable,
              max: familyCareLeaveMax,
            },
            totalUsed,
            totalRemaining,
            lastRefreshed: item.modify_time || new Date().toISOString(),
          };
        });

        this.totalItems =
          Number(pagination.totalItems) || this.employees.length || 0;
        this.totalPages = Number(pagination.totalPages) || 1;
      } catch (error) {
        console.error("Error fetching leave balances:", error);
        this.employees = [];
        this.totalItems = 0;
        this.fetchError = true;
        this.$emit("error", {
          title: "取得假期餘額失敗",
          message:
            error?.response?.data?.message || error.message || "請稍後再試",
        });
      } finally {
        this.loading = false;
      }
    },
    openAddQuotaDialog(employee) {
      this.selectedLeaveField = null;
      this.quotaDays = null;
      this.quotaError = "";
      if (employee && employee.leaveBalanceId) {
        this.selectedLeaveBalanceIds = [employee.leaveBalanceId];
        this.selectedEmployeeNames = [employee.name || "Unknown"];
      } else {
        this.selectedLeaveBalanceIds = (this.employees || [])
          .map((e) => e.leaveBalanceId)
          .filter(Boolean);
        this.selectedEmployeeNames = (this.employees || [])
          .map((e) => e.name)
          .filter(Boolean);
      }
      this.addQuotaDialog = true;
    },
    closeAddQuotaDialog() {
      this.addQuotaDialog = false;
    },
    async submitAddQuota() {
      this.quotaError = "";
      if (!this.selectedLeaveField) {
        this.quotaError = "請選擇請假類型";
        return;
      }
      const days = Number(this.quotaDays);
      if (!Number.isFinite(days) || days < 0) {
        this.quotaError = "請輸入有效的天數";
        return;
      }
      const minutes = days * 8 * 60;

      const allTargets = (this.employees || []).filter((e) => e.leaveBalanceId);
      const targets = this.selectedLeaveBalanceIds.length
        ? allTargets.filter((e) =>
            this.selectedLeaveBalanceIds.includes(e.leaveBalanceId)
          )
        : allTargets;
      if (targets.length === 0) {
        this.quotaError = "目前沒有可更新的員工資料";
        return;
      }

      this.savingQuota = true;
      try {
        const field = this.selectedLeaveField;

        const requests = targets.map((emp) => {
          const payload = {
            selectedLeaveField: field,
            minutes,
          };
          return this.$axios.post(
            `/api/leave-balances/${emp.leaveBalanceId}/add-balance`,
            payload
          );
        });

        await Promise.all(requests);
        this.addQuotaDialog = false;
        await this.fetchLeaveBalances();
      } catch (error) {
        console.error("Error adding leave balance quota:", error);
        this.quotaError =
          error?.response?.data?.message || "新增額度失敗，請稍後再試";
      } finally {
        this.savingQuota = false;
      }
    },
  },
};
</script>

<style scoped></style>
